import * as THREE from 'three';
import BasicObject from './BasicObject';
import { COLORS_CONST } from '../../helpers/styles';
import { ControlPointObjectType, LinkObjectType, SectionsObjetType } from '../types';
import { TextObject } from './TextObject';

export default class ControlPoint extends BasicObject implements ControlPointObjectType {
  static magnetSensitivity = 0.1;
  static _ray = new THREE.Ray();
  static pointsMagCross: Array<THREE.Vector3> = [];
  private lines: {
    line1: LinkObjectType;
    line2: LinkObjectType;
    lineBridge: Partial<LinkObjectType>;
    lineNearPoints: [THREE.Vector3, THREE.Vector3];
    directNearPoints90?: THREE.Vector3;
  } | null;
  private oldPositionY = 0;
  material: THREE.MeshBasicMaterial;
  linkPointStart: LinkObjectType | null;
  linkPointEnd: LinkObjectType | null;
  linkPointBridge: LinkObjectType | null;
  textObject: TextObject | undefined;
  numberInLine = 0;
  constructor() {
    super();
    this.type = 'control_point';
    this.name = 'control_point';
    this.material = new THREE.MeshBasicMaterial({
      color: COLORS_CONST.blue,
      // side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    });
    this.linkPointStart = null;
    this.linkPointEnd = null;
    this.linkPointBridge = null;
    this.lines = null;

    this.buildGeometry();
  }

  buildGeometry(): void {
    super.buildGeometry();
    this.geometry = new THREE.CircleGeometry(0.15, 32);
    this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
  }
  hoverOn(): void {
    // this.scale.set(1.2, 1, 1.2);
    this.material.color = new THREE.Color(COLORS_CONST.red);
  }
  hoverOff(): void {
    // this.scale.set(1, 1, 1);
    this.material.color = new THREE.Color(COLORS_CONST.blue);
  }
  dragStart() {
    super.dragStart();
    (this.parent as SectionsObjetType).deleteDrainFunnel();

    this.oldPositionY = this.position.y;
    let superPoint = this.linkPointEnd?.lineObject.startPoint.position.clone();
    if (this.linkPointBridge) {
      superPoint = this.linkPointBridge?.lineObject.startPoint.position.clone();
    }
    const superRay = this.linkPointStart?.lineObject.endPoint.position.clone();
    let dir = new THREE.Vector3();
    if (superRay && superPoint) {
      ControlPoint.getCrossPointsMag(superRay, superPoint, this.oldPositionY);
      dir = superRay
        .clone()
        .sub(superPoint)
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2)
        .normalize()
        .negate();
    }

    const l1 = this.linkPointStart?.lineObject;
    const l2 = this.linkPointEnd?.lineObject;
    const bridge = this.linkPointBridge?.lineObject;
    const p1 = this.linkPointStart?.linkPoint;
    const p2 = this.linkPointEnd?.linkPoint;
    const pBridge = this.linkPointBridge?.linkPoint;
    if (l1 && l2 && p1 && p2) {
      this.lines = {
        line1: {
          lineObject: l1,
          linkPoint: p1,
        },
        line2: {
          lineObject: l2,
          linkPoint: p2,
        },
        lineBridge: {
          lineObject: bridge,
          linkPoint: pBridge,
        },
        lineNearPoints: [superPoint || new THREE.Vector3(), superRay || new THREE.Vector3()],
        directNearPoints90: dir,
      };
    } else {
      this.lines = null;
    }
  }
  drag(notMagnet?: boolean) {
    super.drag();
    this.position.setY(this.oldPositionY);

    if (!notMagnet) {
      if (this.lines && this.lines?.directNearPoints90) {
        ControlPoint._ray.set(this.position, this.lines.directNearPoints90);
        const pMiddle = new THREE.Vector3();
        ControlPoint._ray.distanceSqToSegment(
          this.lines?.lineNearPoints[0],
          this.lines?.lineNearPoints[1],
          pMiddle
        );

        if (pMiddle.distanceTo(this.position) < ControlPoint.magnetSensitivity) {
          this.position.copy(pMiddle).setY(this.oldPositionY);
        }
      }

      for (let i = 0; i < ControlPoint.pointsMagCross.length; i++) {
        const p = ControlPoint.pointsMagCross[i];
        if (p.distanceTo(this.position) < ControlPoint.magnetSensitivity) {
          this.position.copy(p).setY(this.oldPositionY);
        }
      }
    }

    if (this.lines) {
      this.lines.line1.lineObject.changePositionPoints(this.lines.line1.linkPoint, this.position);
      this.lines.line2.lineObject.changePositionPoints(this.lines.line2.linkPoint, this.position);
      if (this.lines.lineBridge.lineObject && this.lines.lineBridge.linkPoint) {
        this.lines.lineBridge.lineObject.changePositionPoints(
          this.lines.lineBridge.linkPoint,
          this.position
        );
      }
    }
    if (this.textObject) {
      this.textObject.setPosition(this.position);
    }
  }
  dragEnd() {
    super.dragEnd();
    const parent = this.parent as SectionsObjetType;
    parent.updatePositionControlsPoints();
  }
  updateHeightPoint(newHeight: number) {
    this.position.y = newHeight;
    if (this.textObject) {
      this.textObject.setPosition(this.position);
    }
  }
  removeMesh() {
    super.removeMesh(this);
  }

  static getCrossPointsMag(pRey: THREE.Vector3, pLine: THREE.Vector3, currentY: number) {
    ControlPoint.pointsMagCross.length = 0;
    ControlPoint._ray.set(
      pRey.clone().add(new THREE.Vector3(-100, 0, 0)),
      new THREE.Vector3(1, 0, 0)
    );
    const p1 = new THREE.Vector3();
    ControlPoint._ray.distanceSqToSegment(
      pLine.clone().add(new THREE.Vector3(0, 0, 20)),
      pLine.clone().add(new THREE.Vector3(0, 0, -20)),
      p1
    );

    ControlPoint._ray.set(
      pRey.clone().add(new THREE.Vector3(0, 0, -100)),
      new THREE.Vector3(0, 0, 1)
    );
    const p2 = new THREE.Vector3();
    ControlPoint._ray.distanceSqToSegment(
      pLine.clone().add(new THREE.Vector3(20, 0, 0)),
      pLine.clone().add(new THREE.Vector3(-20, 0, 0)),
      p2
    );
    ControlPoint.pointsMagCross.push(p1.setY(currentY), p2.setY(currentY));
  }
}
